import React from "react";
import Div from "../lib/Div";
import {LinearGradient} from 'expo-linear-gradient';
import MainStyle from "../../MainStyle";
import P from "../lib/P";
import {Image, ScrollView} from "react-native";
import target from "../assets/img/icons/target.svg";
import {Ionicons} from "@expo/vector-icons";
import {BetOutcome} from "../../generated/partner_service_pb";

// Outcome palette for the previous-bet row. WON / LOST keep the canonical
// win-green / loss-red semantics shipped with the rest of the app; the two
// no-money-exchanged outcomes use neutral shades so the row is informational
// without overloading the win/loss palette.
const WON_COLOR = '#1E9428';
const LOST_COLOR = '#FF6262';
const CANCELLED_COLOR = '#F5A623';
const EXPIRED_COLOR = '#76777A';
const DEFAULT_TEXT_COLOR = 'white';

type Props = {
  targetDistanceYards: number, previewedTargetDistanceYards: number, previewedNextTargetYards: number,
  previousTargetDistanceYards: number, previousTargetOutcome: BetOutcome
};

type RowKind = 'previous' | 'current' | 'next';

type Row = {
  key: string;
  testID: string;
  kind: RowKind;
  yards: number;
  /** Color applied to the yardage text. */
  textColor: string;
  /**
   * Whole-row opacity. Used to dim the upcoming-shot row so it visually
   * recedes against the active target above it. Undefined = full opacity.
   */
  opacity?: number;
  /** Icon variant — drives which glyph + color is centered in the row. */
  icon: | { variant: 'check'; color: string } | { variant: 'cross'; color: string } | { variant: 'target' }
    | { variant: 'placeholder' };
};

const previousIcon = (outcome: BetOutcome): Row['icon'] => {
  switch (outcome) {
    case BetOutcome.WON:
      return {variant: 'check', color: WON_COLOR};
    case BetOutcome.LOST:
      return {variant: 'cross', color: LOST_COLOR};
    case BetOutcome.CANCELLED:
      return {variant: 'cross', color: CANCELLED_COLOR};
    case BetOutcome.EXPIRED:
      return {variant: 'cross', color: EXPIRED_COLOR};
    default:
      return {variant: 'target'};
  }
};

const previousTextColor = (outcome: BetOutcome): string => {
  switch (outcome) {
    case BetOutcome.WON:
      return WON_COLOR;
    case BetOutcome.LOST:
      return LOST_COLOR;
    case BetOutcome.CANCELLED:
      return CANCELLED_COLOR;
    case BetOutcome.EXPIRED:
      return EXPIRED_COLOR;
    default:
      return DEFAULT_TEXT_COLOR;
  }
};

/**
 * Three stacked rows describing the participant's bet lifecycle:
 *   - top:    previous shot. Icon + yardage are tinted by outcome
 *             (WON green, LOST red, CANCELLED amber, EXPIRED gray);
 *             UNSPECIFIED hides the row.
 *   - middle: current target. Always rendered with the target glyph —
 *             "current" means "this is what you're shooting at," not
 *             "you've completed it." "Real wins over preview"
 *             precedence: targetDistanceYards when set, else
 *             previewedTargetDistanceYards.
 *   - bottom: next-bet preview from Paylines. Dimmed (opacity 0.5)
 *             so the upcoming shot reads as "not active yet" against
 *             the canonical current row above it.
 *
 * Each row is independent; rows with no data are omitted so the panel
 * collapses naturally between bets / on first shot of the session. The
 * column width and 40px connector between rows match the legacy
 * payout-side spacing so the panel reads as a single aligned timeline.
 */
const TargetsBlock = (props: Props) => {
  const {
    targetDistanceYards, previewedTargetDistanceYards, previewedNextTargetYards, previousTargetDistanceYards,
    previousTargetOutcome
  } = props;
  // "Real wins over preview" — see header comment.
  const currentDistance = targetDistanceYards > 0 ? targetDistanceYards : previewedTargetDistanceYards;

  const rows: Row[] = [];

  const showPrevious = previousTargetDistanceYards > 0 && previousTargetOutcome !== BetOutcome.UNSPECIFIED;

  if (showPrevious) {
    rows.push({
      key: 'previous', testID: 'targets-previous-row', kind: 'previous', yards: previousTargetDistanceYards,
      textColor: previousTextColor(previousTargetOutcome), icon: previousIcon(previousTargetOutcome)
    });

  } else if (currentDistance > 0) {
    // No previous bet to render, but the current row is about to. Push an
    // empty slot so the current row stays in the middle position. Without
    // this, the current row jumps to the top of the panel on the first bet
    // of a session and reads as missing data to players.
    rows.push({
      key: 'previous-placeholder', testID: 'targets-previous-placeholder-row', kind: 'previous', yards: 0,
      textColor: DEFAULT_TEXT_COLOR, icon: {variant: 'placeholder'}
    });
  }

  if (currentDistance > 0) {
    rows.push({
      key: 'current', testID: 'targets-current-row', kind: 'current', yards: currentDistance,
      textColor: DEFAULT_TEXT_COLOR, icon: {variant: 'target'}
    });
  }

  if (previewedNextTargetYards > 0) {
    rows.push({
      key: 'next',
      testID: 'targets-next-row',
      kind: 'next',
      yards: previewedNextTargetYards,
      textColor: DEFAULT_TEXT_COLOR,
      // Dim the whole row (text + icon) so the upcoming-shot preview reads
      // as "next, not active" against the canonical current row above it.
      opacity: 0.5,
      icon: {variant: 'target'}
    });
  }

  return (
    <Div w={120} h={210} rounded={4} borderWidth={1} borderColor="#494A4C" overflow="hidden" position="relative">
      <LinearGradient
        colors={['#131314', 'rgba(19,19,20,0)']} locations={[0, 0.95]} pointerEvents="none"
        style={[MainStyle.absolute, MainStyle.t_30, MainStyle.h_32, MainStyle.w_120, MainStyle.z_i_2]}
      />

      <P bg="#252526" fontFamily="DavisSans-Regular" fs={14} ta="center" lh={30}>Targets</P>

      <ScrollView style={[MainStyle.flex1]} showsVerticalScrollIndicator={false}>
        <Div w={79} my={16} ml={32} column alignItems="center">
          {rows.map((row, i) => (
            <React.Fragment key={row.key}>
              {!!i && <Div w={1} h={40} bg="#76777A"/>}

              <Div testID={row.testID} position="relative" justifyContent="center" opacity={row.opacity}>
                {row.icon.variant === 'placeholder' ? (
                  // Empty slot — preserves the row's height + the connector
                  // spacing above the current row so the layout stays stable
                  // when there is no previous bet to display.
                  <Div w={19} h={19}/>
                ) : (
                  <>
                    <P fontFamily="StratosBoldItalic" fs={18} position="absolute" right={27} color={row.textColor}>
                      {row.yards}y
                    </P>

                    {row.icon.variant === 'check' ? (
                      <Div w={19} h={19} bg="white" rounded={10}>
                        <Ionicons
                          name="checkmark-circle" size={24} color={row.icon.color}
                          style={[MainStyle.absolute, MainStyle.t__4, MainStyle.l__2]}
                        />
                      </Div>
                    ) : row.icon.variant === 'cross' ? (
                      <Div w={19} h={19} bg="white" rounded={10}>
                        <Ionicons
                          name="close-circle" size={24} color={row.icon.color}
                          style={[MainStyle.absolute, MainStyle.t__4, MainStyle.l__2]}
                        />
                      </Div>
                    ) : (
                      <Image source={target as any} style={[MainStyle.w_19, MainStyle.h_19]}/>
                    )}
                  </>
                )}
              </Div>
            </React.Fragment>
          ))}
        </Div>
      </ScrollView>

      <LinearGradient
        colors={['rgba(19,19,20,0)', '#131314']} locations={[0, 0.95]} pointerEvents="none"
        style={[MainStyle.absolute, MainStyle.b_0, MainStyle.h_32, MainStyle.w_120]}
      />
    </Div>
  );
};

export default React.memo(TargetsBlock);
